import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

/**
 * T-389/T-390: Supplier data hook for Command Bar integration
 * Provides supplier search with minimum query length enforcement
 */

export interface Supplier {
  id: number;
  externalId: string;
  slug: string;
  name: string;
  category: string | null;
  brandGroup: string | null;
  primaryContact: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  portalUrl: string | null;
  worldviaProUrl: string;
  baseCommissionRate: string | null;
  isPreferred: boolean;
  tramsLinkNo: number | null;
  travelTypes: string[] | null;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  fetchedAt: string;
}

/**
 * Supplier Card interface (synced from external API)
 * Used for supplier context cards in the UI
 */
export interface SupplierCard {
  id: number;
  slug: string;
  supplierName: string;
  supplierCategory: string | null;
  commissionBase: string | null;
  supplierContactName: string | null;
  supplierContactEmail: string | null;
  supplierBookingPhone: string | null;
  supplierPortalUrl: string | null;
  rawData: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  syncedAt: string;
}

export interface SupplierStats {
  total: number;
  active: number;
  inactive: number;
  lastSync: string | null;
  minQueryLength: number;
}

/**
 * T-390: Minimum query length before supplier search is performed
 * Matches server-side SUPPLIER_MATCHING_CONFIG.MIN_QUERY_LENGTH
 */
export const SUPPLIER_MIN_QUERY_LENGTH = 3;

/**
 * Hook to get all active suppliers
 * Use for pre-loading supplier data in Command Bar
 * @deprecated Unused. Reads the legacy `suppliers` table, a stale 2025-12-08
 * snapshot (see the note on the supplier sync methods in BubbleService). Build
 * supplier features on the AR-378 PRO mirror tables (pro_*) instead.
 */
export function useAllSuppliers(enabled = true) {
  return useQuery<Supplier[]>({
    queryKey: ['/api/suppliers'],
    queryFn: () => apiRequest('GET', '/api/suppliers'),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - suppliers don't change frequently
  });
}

/**
 * Hook to search suppliers by name/category
 * T-390: Returns empty array if query is below minimum length
 * @deprecated Unused; reads the legacy `suppliers` table. See useAllSuppliers.
 */
export function useSupplierSearch(searchTerm: string, enabled = true) {
  // T-390: Enforce minimum query length on client side
  const shouldSearch = enabled && searchTerm.length >= SUPPLIER_MIN_QUERY_LENGTH;

  return useQuery<Supplier[]>({
    queryKey: ['/api/suppliers/search', searchTerm],
    queryFn: () =>
      apiRequest('GET', `/api/suppliers/search?q=${encodeURIComponent(searchTerm)}`),
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds for search results
  });
}

/**
 * Hook to get supplier sync statistics
 * @deprecated Unused; reads the legacy `suppliers` table. See useAllSuppliers.
 */
export function useSupplierStats() {
  return useQuery<SupplierStats>({
    queryKey: ['/api/suppliers/stats'],
    queryFn: () => apiRequest('GET', '/api/suppliers/stats'),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Client-side fuzzy matching for suppliers
 * T-390: Improved matching with minimum query length enforcement
 * @deprecated Unused; matches legacy `suppliers` rows. See useAllSuppliers.
 */
export function fuzzyMatchSupplier(query: string, supplier: Supplier): boolean {
  // T-390: Enforce minimum query length
  if (!query || query.length < SUPPLIER_MIN_QUERY_LENGTH) {
    return false;
  }

  const queryLower = query.toLowerCase().trim();
  const nameLower = supplier.name.toLowerCase();
  const categoryLower = (supplier.category || '').toLowerCase();

  // Direct substring match
  if (nameLower.includes(queryLower) || categoryLower.includes(queryLower)) {
    return true;
  }

  // Word starts matching
  const nameWords = nameLower.split(/\s+/);
  const queryWords = queryLower.split(/\s+/);

  // Check if query starts any name word
  if (nameWords.some(word => word.startsWith(queryLower))) {
    return true;
  }

  // Multi-word matching: all query words must match a name word start
  if (queryWords.length > 1) {
    const allWordsMatch = queryWords.every(qWord =>
      qWord.length >= 2 && nameWords.some(nWord => nWord.startsWith(qWord))
    );
    if (allWordsMatch) {
      return true;
    }
  }

  return false;
}

/**
 * Filter suppliers locally using fuzzy matching
 * T-390: Includes minimum query length check
 * @deprecated Unused; filters legacy `suppliers` rows. See useAllSuppliers.
 */
export function filterSuppliers(suppliers: Supplier[], query: string): Supplier[] {
  if (!query || query.length < SUPPLIER_MIN_QUERY_LENGTH) {
    return [];
  }
  return suppliers.filter(supplier => fuzzyMatchSupplier(query, supplier));
}

// ============================================================================
// TRAMS Supplier Profiles (synced nightly into trams_suppliers table)
// Source-of-truth for supplier + travel-type selection in the invoice flow.
// ============================================================================

export interface TramsSupplierTravelType {
  travelTypeLinkNo: number;
  travelTypeName: string;
}

export interface TramsSupplier {
  profileNo: number;
  name: string;
  isPreferred: boolean;
  isActive: boolean;
  agencyLinkNo: number;
  tourPropertyVisible: boolean;
  tourNameVisible: boolean;
  expediaBookable: boolean;
  travelTypes: TramsSupplierTravelType[];
}

export interface TramsSupplierResponse {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
  suppliers: TramsSupplier[];
}

export function useTramsSuppliers(enabled = true) {
  return useQuery<TramsSupplierResponse>({
    queryKey: ['/api/invoices/trams-suppliers'],
    queryFn: () => apiRequest('GET', '/api/invoices/trams-suppliers?limit=5000'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Travel-type list for a single supplier — drives the travel-type dropdown after supplier selection. */
export interface TramsSupplierTravelTypesResponse {
  supplierId: number;
  supplierName: string;
  tourPropertyVisible: boolean;
  tourNameVisible: boolean;
  expediaBookable: boolean;
  travelTypes: Array<TramsSupplierTravelType & { agencyLinkNo: number }>;
}

export function useTramsSupplierTravelTypes(
  supplierId: number | undefined,
  enabled = true,
) {
  return useQuery<TramsSupplierTravelTypesResponse>({
    queryKey: ['/api/invoices/trams-suppliers', supplierId, 'travel-types'],
    queryFn: () =>
      apiRequest(
        'GET',
        `/api/invoices/trams-suppliers/${supplierId}/travel-types`,
      ),
    enabled: enabled && !!supplierId && supplierId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Supplier Cards hooks (synced from external API)
// Used for supplier context cards in the UI
// ============================================================================

/**
 * Hook to get all supplier cards
 */
export function useAllSupplierCards(enabled = true) {
  return useQuery<SupplierCard[]>({
    queryKey: ['/api/suppliers/cards'],
    queryFn: () => apiRequest('GET', '/api/suppliers/cards'),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to search supplier cards by name/category
 */
export function useSupplierCardSearch(searchTerm: string, enabled = true) {
  const shouldSearch = enabled && searchTerm.length >= SUPPLIER_MIN_QUERY_LENGTH;

  return useQuery<SupplierCard[]>({
    queryKey: ['/api/suppliers/cards/search', searchTerm],
    queryFn: () =>
      apiRequest('GET', `/api/suppliers/cards/search?q=${encodeURIComponent(searchTerm)}`),
    enabled: shouldSearch,
    staleTime: 30 * 1000,
  });
}

/**
 * Client-side fuzzy matching for supplier cards
 */
export function fuzzyMatchSupplierCard(query: string, card: SupplierCard): boolean {
  if (!query || query.length < SUPPLIER_MIN_QUERY_LENGTH) {
    return false;
  }

  const queryLower = query.toLowerCase().trim();
  const nameLower = card.supplierName.toLowerCase();
  const categoryLower = (card.supplierCategory || '').toLowerCase();

  // Direct substring match
  if (nameLower.includes(queryLower) || categoryLower.includes(queryLower)) {
    return true;
  }

  // Word starts matching
  const nameWords = nameLower.split(/\s+/);

  if (nameWords.some(word => word.startsWith(queryLower))) {
    return true;
  }

  return false;
}

/**
 * Build worldvia.pro supplier URL from slug
 */
export function buildSupplierWorldviaUrl(slug: string): string {
  return `https://worldvia.pro/pro-supplier/${slug}`;
}
